import { useRef, useState, useEffect } from "react";

import { css, keyframes } from "@compiled/react";
import { flattenValues } from "@uniformdev/canvas";
import { useIntl } from "react-intl";

import VideoPauseOverlayIcon from "@atlaskit/icon/core/video-pause-overlay";
import VideoPlayOverlayIcon from "@atlaskit/icon/core/video-play-overlay";
import { media } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";

import { Button, Image } from "@/libraries/design/components";
import { wacToken } from "@/libraries/design/foundations";

import { UniformColumnLayout } from "../../column-layout";

interface VideoCarouselSlide {
    team: string;
    mobileVideoSrc: string;
    tabletVideoSrc: string;
    desktopVideoSrc: string;
    mobilePoster?: string;
    tabletPoster?: string;
    desktopPoster?: string;
    ariaLabel?: string;
}

export interface UniformRovoHeroProps {
    defaultHeading: string;
    defaultSubheading?: string;
    videoCarousel: VideoCarouselSlide[];
    primaryCtaLabel?: string;
    primaryCtaHref?: string;
    headingTag?: "h1" | "h2";
    flattenData?: boolean;
}

const UniformRovoHero = ({
    defaultHeading,
    defaultSubheading,
    videoCarousel,
    primaryCtaLabel,
    primaryCtaHref,
    headingTag = "h1",
    flattenData = true
}: // eslint-disable-next-line complexity
UniformRovoHeroProps) => {
    const videoCarouselData = flattenData
        ? (flattenValues(
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              videoCarousel as any
          ) as VideoCarouselSlide[])
        : videoCarousel;

    const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
    const videoContainerRef = useRef<HTMLDivElement | null>(null);

    const [current, setCurrent] = useState(-1);
    const currentRef = useRef(current);
    const [playing, setPlaying] = useState(true);
    const [durations, setDurations] = useState<number[]>(
        Array(videoCarouselData.length).fill(0)
    );

    const [fillBarResetKey, setFillBarResetKey] = useState(0);

    // @ts-ignore
    const prevViewportWidth = useRef(
        typeof window !== "undefined" ? window.innerWidth : 0
    );
    const [viewportWidth, setViewportWidth] = useState(
        typeof window !== "undefined" ? window.innerWidth : 0
    );

    useEffect(() => {
        currentRef.current = current;
    }, [current]);

    // On-load
    useEffect(() => {
        let hasRun = false;
        let observer: IntersectionObserver | null = null;
        let wheelTimeout: ReturnType<typeof setTimeout> | null = null;
        let keyTimeout: ReturnType<typeof setTimeout> | null = null;

        function runEffectLogic() {
            if (hasRun) return;
            hasRun = true;

            const handleFirstInteraction = () => {
                if (currentRef.current === -1) {
                    setCurrent(0);
                }
            };

            const handleFirstClick = () => {
                handleFirstInteraction();
                enableScrollEvents();
            };

            // Scroll blocking
            function preventDefault(e: Event) {
                e.preventDefault();
                handleFirstInteraction();
            }

            function onWheel(e: WheelEvent) {
                preventDefault(e);
                if (wheelTimeout) clearTimeout(wheelTimeout);
                wheelTimeout = setTimeout(enableScrollEvents, 20);
            }

            function onKeyDown(e: KeyboardEvent) {
                preventDefault(e);
                if (keyTimeout) clearTimeout(keyTimeout);
                keyTimeout = setTimeout(enableScrollEvents, 20);
            }

            function onTouchMove(e: TouchEvent) {
                preventDefault(e);
            }

            function onTouchEnd() {
                enableScrollEvents();
            }

            function blockScrollEvents() {
                window.addEventListener("wheel", onWheel, { passive: false });
                window.addEventListener("touchmove", onTouchMove, {
                    passive: false
                });
                window.addEventListener("touchend", onTouchEnd, {
                    passive: false
                });
                window.addEventListener("touchcancel", onTouchEnd, {
                    passive: false
                });
                window.addEventListener("keydown", onKeyDown, {
                    passive: false
                });
            }

            function enableScrollEvents() {
                window.removeEventListener("wheel", onWheel);
                window.removeEventListener("touchmove", onTouchMove);
                window.removeEventListener("touchend", onTouchEnd);
                window.removeEventListener("touchcancel", onTouchEnd);
                window.removeEventListener("keydown", onKeyDown);
                if (wheelTimeout) clearTimeout(wheelTimeout);
                if (keyTimeout) clearTimeout(keyTimeout);
            }

            blockScrollEvents();

            // Video container click interactions
            const container = videoContainerRef.current;
            if (container) {
                container.addEventListener("pointerdown", handleFirstClick, {
                    once: true
                });
            }

            // Video durations
            videoCarouselData.forEach((_, idx) => {
                const video = videoRefs.current[idx];
                if (!video) return;
                if (video.readyState >= 1) {
                    setDurations((durs) => {
                        const copy = [...durs];
                        copy[idx] = video.duration;
                        return copy;
                    });
                } else {
                    video.onloadedmetadata = () => {
                        setDurations((durs) => {
                            const copy = [...durs];
                            copy[idx] = video.duration;
                            return copy;
                        });
                    };
                }
            });

            // Cleanup
            return () => {
                enableScrollEvents();
                if (container) {
                    container.removeEventListener(
                        "pointerdown",
                        handleFirstInteraction
                    );
                    container.removeEventListener(
                        "keydown",
                        handleFirstInteraction
                    );
                }
            };
        }

        // Intersection Observer setup
        observer = new IntersectionObserver(
            (entries) => {
                const entry = entries[0];
                if (entry && entry.isIntersecting) {
                    runEffectLogic();
                    observer?.disconnect();
                }
            },
            { threshold: 0.1 }
        );
        if (videoContainerRef.current) {
            observer.observe(videoContainerRef.current);
        }

        // Cleanup
        return () => {
            if (observer && videoContainerRef.current) {
                observer.unobserve(videoContainerRef.current);
            }
        };
    }, []);

    // Play next video on end, looping
    const handleEnded = () => {
        const currVideo = videoRefs.current[current];
        if (currVideo) {
            currVideo.pause();
            currVideo.currentTime = 0;
        }

        setCurrent((prev) => (prev + 1) % videoCarouselData.length);
    };

    // Pause/play current video
    useEffect(() => {
        if (current === -1) return;
        const video = videoRefs.current[current];
        if (video) {
            if (playing) {
                if (video.ended) {
                    video.currentTime = 0;
                }
                // eslint-disable-next-line @typescript-eslint/no-empty-function
                video.play().catch(() => {});
            } else {
                video.pause();
            }
        }
    }, [playing, current]);

    // Pause/play toggle
    const togglePlay = () => {
        setPlaying((p) => !p);
    };

    // Jump to a specific video and start from beginning
    const handleTeamClick = (idx: number) => {
        if (idx === current) return;

        const currVideo = videoRefs.current[current];
        if (currVideo) {
            currVideo.pause();
            currVideo.currentTime = 0;
        }

        setCurrent(idx);
        setPlaying(true);
    };

    const currentDuration = durations[current] || 10;

    // Responsive asset type
    useEffect(() => {
        let resizeTimeout: number | undefined;
        const breakpoints = [768, 1024];
        // Store previous width outside the handler so it persists
        const prevViewportWidth = { current: window.innerWidth };
        function crossedBreakpoint(prev: number, curr: number) {
            return breakpoints.some(
                (bp) => (prev < bp && curr >= bp) || (prev >= bp && curr < bp)
            );
        }
        const handleResizeEnd = () => {
            const currWidth = window.innerWidth;
            if (crossedBreakpoint(prevViewportWidth.current, currWidth)) {
                setFillBarResetKey((k) => k + 1);
            }
            prevViewportWidth.current = currWidth;
        };
        const handleResize = () => {
            setViewportWidth(window.innerWidth);
            if (resizeTimeout !== undefined) {
                clearTimeout(resizeTimeout);
            }
            resizeTimeout = window.setTimeout(handleResizeEnd, 300);
        };
        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("resize", handleResize);
            if (resizeTimeout !== undefined) {
                clearTimeout(resizeTimeout);
            }
        };
    }, []);

    const assetType =
        viewportWidth >= 1024
            ? "desktop"
            : viewportWidth >= 768
            ? "tablet"
            : "mobile";

    const intl = useIntl();

    return (
        <section css={sectionCss}>
            <div
                css={[
                    desktopCss,
                    desktopLeftAssetCss,
                    css({
                        transform:
                            current !== -1
                                ? "translateX(-120px)"
                                : "translateX(0)"
                    })
                ]}
            >
                <Image
                    src="https://dam-cdn.atl.orangelogic.com/AssetLink/ih4h04rnm06355wnp2arca3f4gkg0258.webp"
                    alt=""
                    style={desktopAssetStyle}
                />
            </div>
            <div
                css={[
                    desktopCss,
                    desktopRightAssetCss,
                    css({
                        transform:
                            current !== -1
                                ? "translateX(120px)"
                                : "translateX(0)"
                    })
                ]}
            >
                <Image
                    src="https://dam-cdn.atl.orangelogic.com/AssetLink/3qji3u3j76e5i63yjsj7pnko2npm30n4.webp"
                    alt=""
                    style={desktopAssetStyle}
                />
            </div>
            <div css={containerCss}>
                <UniformColumnLayout layout="8">
                    <div css={headingContainerCss}>
                        {defaultSubheading && (
                            <div
                                className="body-lg subheading"
                                css={subheadingCss}
                            >
                                {defaultSubheading}
                            </div>
                        )}
                        <div css={headingCss}>
                            {headingTag === "h1" ? (
                                <h1 css={mainHeadingCss}>{defaultHeading}</h1>
                            ) : (
                                <h2 css={mainHeadingCss}>{defaultHeading}</h2>
                            )}
                        </div>
                        {primaryCtaLabel && primaryCtaHref && (
                            <div css={ctaContainerCss}>
                                <Button
                                    href={primaryCtaHref}
                                    css={ctaCss}
                                    dataEventProps={{
                                        "data-product-key": "uniform",
                                        "data-event": "clicked",
                                        "data-event-component": "linkButton",
                                        "data-event-container": "heroSection"
                                    }}
                                >
                                    {primaryCtaLabel}
                                </Button>
                            </div>
                        )}
                    </div>
                </UniformColumnLayout>
                <div
                    css={[videoContainerCss, videoDimensionsCss]}
                    ref={videoContainerRef}
                >
                    <div css={videoCss}>
                        {videoCarouselData.map(
                            (
                                {
                                    team,
                                    mobileVideoSrc,
                                    tabletVideoSrc,
                                    desktopVideoSrc,
                                    mobilePoster,
                                    tabletPoster,
                                    desktopPoster,
                                    ariaLabel
                                },
                                idx
                            ) => (
                                <video
                                    key={team}
                                    ref={(el) => (videoRefs.current[idx] = el)}
                                    src={
                                        assetType === "desktop"
                                            ? desktopVideoSrc
                                            : assetType === "tablet"
                                            ? tabletVideoSrc
                                            : mobileVideoSrc
                                    }
                                    muted={true}
                                    playsInline={true}
                                    css={[
                                        videoDimensionsCss,
                                        css({
                                            visibility:
                                                idx === current ||
                                                (idx === 0 && current === -1)
                                                    ? "visible"
                                                    : "hidden",
                                            height: `${
                                                idx === current ||
                                                (idx === 0 && current === -1)
                                                    ? "100%"
                                                    : 0
                                            } !important`
                                        })
                                    ]}
                                    onEnded={handleEnded}
                                    autoPlay={idx === current && playing}
                                    controls={false}
                                    aria-label={ariaLabel}
                                    preload="auto"
                                    poster={
                                        (assetType === "desktop"
                                            ? desktopPoster
                                            : assetType === "tablet"
                                            ? tabletPoster
                                            : mobilePoster) ?? undefined
                                    }
                                />
                            )
                        )}
                    </div>
                </div>
                {current !== -1 && (
                    <UniformColumnLayout layout="12">
                        <div css={controlContainerCss}>
                            <button css={buttonCss} onClick={togglePlay}>
                                <div
                                    css={buttonCirlceCss}
                                    data-event="clicked"
                                    data-event-component="button"
                                    data-event-container="heroSection"
                                >
                                    {playing ? (
                                        <VideoPauseOverlayIcon
                                            label={intl.formatMessage({
                                                id: "rovo-hero.pause",
                                                defaultMessage: "Pause"
                                            })}
                                        />
                                    ) : (
                                        <VideoPlayOverlayIcon
                                            label={intl.formatMessage({
                                                id: "rovo-hero.play",
                                                defaultMessage: "Play"
                                            })}
                                        />
                                    )}
                                </div>
                            </button>
                            <div css={barContainerCss}>
                                {videoCarouselData.map(({ team }, idx) => (
                                    <button
                                        key={team}
                                        onClick={() => handleTeamClick(idx)}
                                        data-event="clicked"
                                        data-event-component="button"
                                        data-event-container="heroSection"
                                        css={[
                                            barbuttonCss,
                                            css({
                                                color:
                                                    idx === current
                                                        ? wacToken(
                                                              "color.primary.blue"
                                                          )
                                                        : wacToken(
                                                              "color.neutral.light800"
                                                          ),

                                                ["&:hover"]: {
                                                    cursor:
                                                        idx === current
                                                            ? "auto"
                                                            : "pointer"
                                                }
                                            })
                                        ]}
                                    >
                                        <div
                                            css={[
                                                barCss,
                                                idx === 0 && firstBarCss,
                                                idx ===
                                                    videoCarouselData.length -
                                                        1 && lastBarCss
                                            ]}
                                        >
                                            {idx === current && (
                                                <div
                                                    key={fillBarResetKey}
                                                    className="fillBar"
                                                    css={[
                                                        fillBarCss,
                                                        css({
                                                            animation: `${fill} ${currentDuration}s linear forwards`,
                                                            animationPlayState:
                                                                playing
                                                                    ? "running"
                                                                    : "paused"
                                                        })
                                                    ]}
                                                />
                                            )}
                                        </div>
                                        {team}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </UniformColumnLayout>
                )}
            </div>
            <div css={mobileCss}>
                <Image
                    src="https://dam-cdn.atl.orangelogic.com/AssetLink/6ts3bd426400kq6ds07w1208q7a038r0.webp"
                    alt=""
                    style={imageStyles}
                    css={mobileCss}
                />
            </div>
            <div css={tabletCss}>
                <Image
                    src="https://dam-cdn.atl.orangelogic.com/AssetLink/6325jfp76jcjym74os216mopusw7m420.webp"
                    alt=""
                    style={imageStyles}
                />
            </div>
        </section>
    );
};

export default UniformRovoHero;

const desktopAssetStyle = {
    width: "787px"
};

const desktopLeftAssetCss = css({
    transition: "transform 600ms ease-in-out",

    position: "absolute",
    zIndex: 1,
    insetInlineStart: "-465px",

    [media.only.md]: {
        insetInlineStart: "-680px"
    }
});

const desktopRightAssetCss = css({
    transition: "transform 600ms ease-in-out",

    position: "absolute",
    zIndex: 1,
    insetInlineEnd: "-465px",

    [media.only.md]: {
        insetInlineEnd: "-680px"
    }
});

const desktopCss = css({
    [media.below.md]: {
        display: "none"
    }
});

const tabletCss = css({
    [media.above.md]: {
        display: "none"
    },
    [media.below.sm]: {
        display: "none"
    }
});

const mobileCss = css({
    [media.above.sm]: {
        display: "none"
    }
});

const imageStyles = {
    width: "100%",
    display: "block"
};

const sectionCss = css({
    overflow: "hidden",
    position: "relative",
    backgroundColor: wacToken("color.neutral.light100")
});

const containerCss = css({
    overflowX: "hidden",
    paddingBlock: token("space.800"),

    [media.below.sm]: {
        paddingBlock: token("space.300")
    }
});

const subheadingCss = css({
    "&.body-lg.subheading": {
        fontFamily: wacToken("font.charlie.display"),
        lineHeight: "1.5rem",

        [media.only.sm]: {
            fontSize: "1.25rem"
        },

        [media.below.sm]: {
            fontSize: "0.875rem"
        }
    }
});

const headingContainerCss = css({
    textAlign: "center"
});

const headingCss = css({
    alignItems: "center",
    marginBlockStart: token("space.200")
});

const mainHeadingCss = css({
    "margin": 0,

    "&:lang(en)": {
        textWrap: "balance"
    }
});

const ctaContainerCss = css({
    display: "flex",
    flexWrap: "wrap",
    gap: token("space.150"),
    justifyContent: "center",
    marginBlock: token("space.300"),

    [media.below.sm]: {
        marginBlock: token("space.200")
    }
});

const ctaCss = css({
    textWrap: "nowrap",

    [media.below.sm]: {
        fontSize: "1rem",
        lineHeight: "1.25rem",
        fontWeight: "500"
    }
});

const videoContainerCss = css({
    position: "relative",
    insetInlineStart: "50%",
    transform: "translateX(-50%)"
});

const videoDimensionsCss = css({
    width: "1168px",
    height: "312px",

    [media.only.sm]: {
        width: "1023px",
        height: "324px"
    },

    [media.below.sm]: {
        width: "375px",
        height: "164px"
    }
});

const videoCss = css({
    width: "100%",
    height: "100%",
    display: "block",
    textAlign: "center",
    position: "relative",
    overflow: "hidden"
});

const fadeIn = keyframes({
    from: {
        opacity: 0,
        height: 0
    },
    to: {
        opacity: 1,
        height: "100%"
    }
});

const controlContainerCss = css({
    marginBlockStart: token("space.600"),
    marginInline: "auto",
    width: "100%",
    maxWidth: "768px",
    gap: token("space.150"),
    animation: `${fadeIn} 0.5s ease-in-out`,

    display: "grid",
    gridTemplateColumns: "auto min-content",

    [media.below.sm]: {
        marginBlockStart: token("space.150"),
        gridTemplateColumns: "auto",
        justifyItems: "end"
    }
});

const barContainerCss = css({
    display: "flex",
    alignItems: "flex-start",
    gap: token("space.150"),
    width: "100%"
});

const buttonCss = css({
    height: "44px",
    width: "44px",
    margin: "-6px",

    backgroundColor: "transparent",
    border: "none",
    borderRadius: "100%",

    gridRow: "1",
    gridColumn: "2",

    ["&:hover"]: {
        cursor: "pointer"
    },

    [media.below.sm]: {
        gridRow: "1",
        gridColumn: "1"
    }
});

const buttonCirlceCss = css({
    height: "32px",
    width: "32px",
    backgroundColor: wacToken("color.neutral.dark1000"),
    border: "none",
    borderRadius: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",

    ["&:hover"]: {
        cursor: "pointer",
        backgroundColor: wacToken("color.neutral.dark800")
    },

    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    span: {
        color: `${wacToken("color.neutral.dark300")} !important`
    }
});

const firstBarCss = css({
    borderRadius: "10px 0 0 10px"
});

const lastBarCss = css({
    borderRadius: "0 10px 10px 0"
});

const barbuttonCss = css({
    background: "none",
    border: "none",
    padding: 0,
    width: "100%",
    fontFamily: wacToken("font.charlie.display"),
    fontSize: "1rem",
    fontWeight: 700,
    lineHeight: "1.5rem"
});

const barCss = css({
    height: "8px",
    width: "100%",
    position: "relative",
    overflow: "hidden",
    backgroundColor: wacToken("color.neutral.light300"),
    marginBlock: `${token("space.150")} ${token("space.100")}`
});

const fillBarCss = css({
    height: "100%",
    width: "100%",
    backgroundColor: wacToken("color.primary.blue"),
    position: "absolute",
    insetInlineStart: 0,
    insetBlockStart: 0
});

const fill = keyframes({
    from: { width: "0%" },
    to: { width: "100%" }
});
